import React from "react";
import { Download } from 'lucide-react';
import { enrichDataWithLabels } from "../../utils/getDataLabelForDataviz";

export default function TopLineComponent({ chart }) {
  // defensive defaults in case some fields are missing
  const chart_config = chart?.chart_config || {};
  const metadata = chart?.metadata || {};
  const categoryField = chart_config.x_axis?.field || "category";
  const rawData = Array.isArray(chart?.data) ? chart.data : [];
  const data = enrichDataWithLabels(rawData, metadata, categoryField);

  const categoryFieldLabel = chart_config.x_axis?.label || "category";
  const valueField = chart_config.y_axes?.[0]?.field || "count";
  const title = chart_config.title || chart?.id || "Chart";
  const lastUpdated = metadata.last_updated || metadata.lastUpdated || null;
  const color = chart_config.y_axes?.[0]?.color || "#10b981";

  const total = data.reduce((sum, row) => sum + (Number(row[valueField]) || 0), 0);

  const handleDownloadCSV = () => {
	const topLine = `${categoryField}, ${title},`
	const headers = ["Response", "Count", "Percentage"];
	const csvRows = [topLine];
	csvRows.push(headers.join(","))

	data.forEach((row) => {
	  const value = Number(row[valueField]) || 0;
	  const pct = total > 0 ? (value / total) * 100 : 0;
	  const responseLabel = row.label ?? row[categoryField] ?? "";

	  const csvRow = [
		`"${String(responseLabel).replace(/"/g, '""')}"`, // escape quotes per CSV
		value,
		pct.toFixed(1)
	  ];

	  csvRows.push(csvRow.join(","));
	});

	// total row
	csvRows.push(["Total", total, total > 0 ? "100" : "0"].join(","));

	// join with proper newline escape sequence to avoid syntax errors
	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });

	const url = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;

	const safeId = chart?.id ? String(chart.id).replace(/[^a-z0-9-_.]/gi, "-") : "chart";
	a.download = `${safeId}-table-view-${new Date().toISOString().split("T")[0]}.csv`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
  };

  return (
	<div className="border shadow-sm p-4 relative bg-white text-gray-900">
	  <button
		onClick={handleDownloadCSV}
		className="absolute top-2 right-2 p-2 hover:bg-gray-100 transition"
		title="Download as CSV"
		aria-label="Download as CSV"
	  >
		<Download></Download>
	  </button>

	  <div className="mb-4">
		<h2 className="text-xl font-bold">({categoryField}) - {title}</h2>
	  </div>

	  <div className="overflow-x-auto">
		<table className="w-full text-sm">
		  <thead className="bg-gray-100">
			<tr>
			  <th className="px-4 py-2 text-left w-1/4">Response</th>
			  <th className="px-4 py-2 text-left w-2/4">Bar</th>
			  <th className="px-4 py-2 text-center w-1/8">%</th>
			  <th className="px-4 py-2 text-center w-1/8">Count</th>
			</tr>
		  </thead>

		  <tbody>
			{data.map((row, index) => {
				const value = Number(row[valueField]) || 0;
				const pct = total > 0 ? (value / total) * 100 : 0;
				const label =  row[categoryFieldLabel] ?? String(row[categoryField] ?? "") + (row.label ? ` - ${row.label}`: "");

			  	return (
					<tr key={index} className="border-t border-gray-200">
					<td className="px-4 py-3 font-medium">{label}</td>

						<td className="px-4 py-3">
							<div className="h-3 w-full bg-gray-200 rounded relative overflow-hidden">
								<div
									className="h-full absolute left-0 top-0"
									style={{ width: `${pct}%`, backgroundColor: color }}
								/>
							</div>
						</td>

					<td className="px-4 py-3 text-center">{pct.toFixed(1)}%</td>
					<td className="px-4 py-3 text-center font-semibold">{value}</td>
				</tr>
			  );
			})}

			<tr className="border-t border-gray-300 font-semibold">
				<td className="px-4 py-3">Total</td>
				<td className="px-4 py-3"></td>
				<td className="px-4 py-3 text-center">{total > 0 ? "100%" : "0%"}</td>
				<td className="px-4 py-3 text-center">{total}</td>
			</tr>
		  </tbody>
		</table>
	  </div>

		<div className="absolute bottom-2 right-4 text-xs text-gray-500">
			{lastUpdated ? `Last updated: ${new Date(lastUpdated).toLocaleString()}` : ""}
		</div>
	</div>
  );
}
